import { useState } from "react";
import { Can } from "@core/permissions";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  FeedbackState,
  PortalButton,
  RowIconAction,
  SidePanel,
  SidePanelHeader,
} from "@portal/common";
import { ReferralActionsPanel } from "../referrals";
import { formatShortDate } from "../vehicles/dateFormat";
import type { Progress, SupportingPurchase } from "./progressApi";
import { useProgressDetail } from "./progressQueries";
import { GoalValidationModal } from "./GoalValidationModal";
import { PrizeDeliveryModal } from "./PrizeDeliveryModal";
import { Ring } from "./Ring";

const STATUS_LABEL: Record<Progress["status"], string> = {
  // GP-01-A (resolución humana 2026-09-13): mismos literales fijados en
  // GrandPrizeProgressScreen.tsx.
  pending: "Pendiente",
  expired: "Vencido",
  in_progress: "En curso",
  goal_reached: "Meta alcanzada",
  prize_delivered: "Ganador entregado",
  closed_on_inactivation: "Cerrado por baja",
};

// PG-201: línea secundaria "{fecha} · {quién registró}", tipografía y color
// del patrón que el prototipo ya usa para el mismo dato en el panel hermano
// de esta pantalla (`FichaReferidoPanel`, admin.jsx:1655). Si `registeredBy`
// es nulo se omite el segmento entero: ni guion, ni relleno.
function PurchaseSecondaryLine({ purchase }: { purchase: SupportingPurchase }) {
  const parts = [formatShortDate(purchase.date)];
  if (purchase.registeredBy) parts.push(purchase.registeredBy);
  return (
    <div
      style={{
        font: `400 11.5px/1.6 ${tokens.font.ui}`,
        color: tokens.text.tertiary,
      }}
    >
      {parts.join(" · ")}
    </div>
  );
}

function PurchaseRow({
  purchase,
  onOpenReferral,
  faded,
}: {
  purchase: SupportingPurchase;
  onOpenReferral: (referralId: string) => void;
  faded?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 18px",
        borderBottom: `1px solid ${tokens.surface.line}`,
        // PG-001: fila atenuada con la opacidad de fila inactiva ya
        // presente en el prototipo (admin.jsx:2046).
        opacity: faded ? 0.62 : 1,
      }}
    >
      <div>
        <div style={{ font: `600 12.5px ${tokens.font.ui}` }}>
          {purchase.referralName}
        </div>
        <PurchaseSecondaryLine purchase={purchase} />
      </div>
      <RowIconAction
        label="Ver ficha del referido"
        icon="eye"
        onClick={() => onOpenReferral(purchase.referralId)}
      />
    </div>
  );
}

export function ProgressDetailSheet({
  progressId,
  onClose,
}: {
  progressId: string;
  onClose: () => void;
}) {
  const detail = useProgressDetail(progressId);
  const [openReferralId, setOpenReferralId] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [delivering, setDelivering] = useState(false);

  const data = detail.data;
  const surplus = data ? Math.max(0, data.progress - data.goal) : 0;
  const canOfferValidation =
    Boolean(data) && data!.status === "goal_reached" && !data!.validatedAt;
  const canOfferDelivery =
    Boolean(data) &&
    data!.status === "goal_reached" &&
    Boolean(data!.validatedAt);

  return (
    <SidePanel label="Detalle del avance" width={560} onClose={onClose}>
      <SidePanelHeader
        label="Detalle del avance"
        title={data?.member.legalName ?? "—"}
        titleFontSize={25}
        titleLineHeight={1.06}
      >
        {data ? (
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <Badge
              tone={data.status === "goal_reached" ? "achievement" : "neutral"}
            >
              {STATUS_LABEL[data.status]}
            </Badge>
            <Badge
              tone={data.member.accountActivated ? "silver" : "achievement"}
            >
              Cuenta {data.member.accountActivated ? "activada" : "no activada"}
            </Badge>
          </div>
        ) : null}
      </SidePanelHeader>
      <div style={{ flex: 1, overflowY: "auto", padding: "22px 26px 30px" }}>
        {detail.isLoading ? <FeedbackState kind="loading" /> : null}
        {detail.isError ? <FeedbackState kind="error" /> : null}
        {data ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 22,
                padding: 18,
                borderRadius: tokens.radius.sm,
                border: `1px solid ${tokens.surface.line}`,
                marginBottom: 16,
              }}
            >
              <Ring n={data.progress} goal={data.goal} size={116} />
              <div
                style={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px 14px",
                }}
              >
                {[
                  ["Inicio del conteo", formatShortDate(data.startsOn)],
                  ["Cuenta desde", formatShortDate(data.countsFrom)],
                  ["Meta", `${data.goal} autos`],
                  ...(data.windowMonths
                    ? [["Ventana", `${data.windowMonths} meses por compra`]]
                    : []),
                ].map(([label, value]) => (
                  <div key={label}>
                    <div
                      className="label"
                      style={{ fontSize: 9.5, marginBottom: 4 }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        font: `400 12.5px ${tokens.font.ui}`,
                        color: tokens.text.secondary,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                border: `1px solid ${tokens.surface.line}`,
                borderRadius: tokens.radius.sm,
                overflow: "hidden",
                marginBottom: 16,
              }}
            >
              <div style={{ padding: "16px 18px 12px" }}>
                <span className="label">Compras que sustentan el avance</span>
              </div>
              {data.purchasesInWindow.length === 0 ? (
                <div
                  style={{
                    padding: "0 18px 16px",
                    color: tokens.text.tertiary,
                    font: `400 12px ${tokens.font.ui}`,
                  }}
                >
                  Todavía no hay compras registradas.
                </div>
              ) : (
                data.purchasesInWindow.map((purchase) => (
                  <PurchaseRow
                    key={`${purchase.referralId}-${purchase.date}`}
                    purchase={purchase}
                    onOpenReferral={setOpenReferralId}
                  />
                ))
              )}
            </div>

            {/* PG-001: segunda lista, aditiva, debajo de la vigente, con la
                misma fila y la opacidad de fila inactiva del prototipo. */}
            {data.purchasesOutsideWindow.length > 0 ? (
              <div
                style={{
                  border: `1px solid ${tokens.surface.line}`,
                  borderRadius: tokens.radius.sm,
                  overflow: "hidden",
                  marginBottom: 16,
                }}
              >
                <div style={{ padding: "16px 18px 12px" }}>
                  <span className="label">Fuera de la ventana</span>
                </div>
                {data.purchasesOutsideWindow.map((purchase) => (
                  <PurchaseRow
                    key={`${purchase.referralId}-${purchase.date}-outside`}
                    purchase={purchase}
                    onOpenReferral={setOpenReferralId}
                    faded
                  />
                ))}
              </div>
            ) : null}

            <Can module="prizes" action="canEdit">
              {canOfferValidation ? (
                <PortalButton
                  tone="primary"
                  size="small"
                  onClick={() => setValidating(true)}
                >
                  Validar la meta
                </PortalButton>
              ) : null}
            </Can>
            <Can module="prizes" action="canCreate">
              {canOfferDelivery ? (
                <PortalButton
                  tone="primary"
                  size="small"
                  onClick={() => setDelivering(true)}
                  style={{ marginLeft: canOfferValidation ? 10 : 0 }}
                >
                  Marcar ganador entregado
                </PortalButton>
              ) : null}
            </Can>
          </>
        ) : null}
      </div>

      {openReferralId ? (
        <ReferralActionsPanel
          referralId={openReferralId}
          progressId={progressId}
          {...(data?.member.id ? { memberId: data.member.id } : {})}
          onClose={() => setOpenReferralId(null)}
        />
      ) : null}
      {validating && data ? (
        <GoalValidationModal
          progress={data}
          onClose={() => setValidating(false)}
        />
      ) : null}
      {delivering && data ? (
        <PrizeDeliveryModal
          progress={data}
          surplus={surplus}
          onClose={() => setDelivering(false)}
        />
      ) : null}
    </SidePanel>
  );
}
